import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  ButtonGroup,
  Button,
  Spinner,
} from "reactstrap"
import ReactApexChart from "react-apexcharts"
import { useSelector } from "react-redux"
import axios from "axios"

const ReachChart = () => {
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const organizations = useSelector(
    state => state.organizationReducer.organizations,
  )
  const isConsolidatedView = useSelector(
    state => state.organizationReducer.isConsolidatedView,
  )
  const mode = useSelector(state => state.Layout.layoutMode)

  const [dataGranularity, setDataGranularity] = useState("monthly")
  const [isLoading, setIsLoading] = useState(false)

  const [chartData, setChartData] = useState({
    options: {
      chart: { toolbar: { show: false } },
      xaxis: { categories: [] },
      yaxis: { title: { text: "Page Views" } },
    },
    series: [],
  })

  // -------------------------------------------
  // GET ALL FB PAGES DEPENDING ON VIEW MODE
  // -------------------------------------------
  const getPages = () => {
    return isConsolidatedView
      ? organizations.flatMap(o => o.Organization.FacebookIntegrations)
      : activeOrganization.FacebookIntegrations
  }

  // -------------------------------------------
  // FETCH REACH FOR ONE PAGE
  // -------------------------------------------
  const fetchPageReach = async (pageId, token, orgName) => {
    if (dataGranularity === "daily") {
      const now = new Date()
      const twoWeeksAgo = new Date()
      twoWeeksAgo.setDate(now.getDate() - 14)

      const since = Math.floor(twoWeeksAgo.getTime() / 1000)
      const until = Math.floor(now.getTime() / 1000)

      const resp = await axios.get(
        `https://graph.facebook.com/v20.0/${pageId}/insights`,
        {
          params: {
            metric: "page_views_total",
            period: "day",
            since,
            until,
            access_token: token,
          },
        },
      )

      const values = resp.data.data[0].values

      return {
        name: orgName,
        categories: values.map(v =>
          new Date(v.end_time).toLocaleDateString("default", {
            weekday: "short",
            month: "short",
            day: "numeric",
          }),
        ),
        data: values.map(v => v.value),
      }
    }

    // ------------ MONTHLY MODE (last 12 months) ------------
    const now = new Date()
    const oneYearAgo = new Date()
    oneYearAgo.setFullYear(now.getFullYear() - 1)

    const periods = []
    let start = new Date(oneYearAgo)

    while (start < now) {
      const end = new Date(start)
      end.setDate(end.getDate() + 29)

      if (end > now) end.setTime(now.getTime())

      periods.push({
        since: Math.floor(start.getTime() / 1000),
        until: Math.floor(end.getTime() / 1000),
      })

      start.setDate(start.getDate() + 30)
    }

    const monthlyTotals = {}

    for (const p of periods) {
      const resp = await axios.get(
        `https://graph.facebook.com/v20.0/${pageId}/insights`,
        {
          params: {
            metric: "page_views_total",
            period: "day",
            since: p.since,
            until: p.until,
            access_token: token,
          },
        },
      )

      const values = resp.data.data[0].values

      values.forEach(v => {
        const monthKey = new Date(v.end_time).toLocaleString("default", {
          month: "short",
          year: "numeric",
        })

        monthlyTotals[monthKey] = (monthlyTotals[monthKey] || 0) + v.value
      })
    }

    const sortedMonths = Object.keys(monthlyTotals)
      .sort((a, b) => new Date(a) - new Date(b))
      .slice(-12)

    return {
      name: orgName,
      categories: sortedMonths,
      data: sortedMonths.map(m => monthlyTotals[m]),
    }
  }

  // -------------------------------------------
  // MAIN FETCH FUNCTION
  // -------------------------------------------
  const fetchInsights = async () => {
    setIsLoading(true)

    try {
      const pages = getPages()

      const allSeries = []
      let masterCategories = []

      for (const page of pages) {

        const pageData = await fetchPageReach(
          page.pageId,
          page.pageToken,
          organizations.find(
            o =>
              o.Organization?.FacebookIntegrations[0]?.pageId === page.pageId ||
              o.id === page.pageId,
          )?.Organization?.name || "Organization",
        )

        // Set categories only once (they are the same scale)
        if (masterCategories.length === 0) {
          masterCategories = pageData.categories
        }

        allSeries.push({
          name: pageData.name,
          data: pageData.data,
        })
      }

      setChartData({
        options: {
          ...chartData.options,
          xaxis: { categories: masterCategories },
        },
        series: allSeries,
      })
    } catch (err) {
      console.error("Error loading reach chart", err)
    }

    setIsLoading(false)
  }

  useEffect(() => {
    fetchInsights()
  }, [dataGranularity, activeOrganization, isConsolidatedView])

  return (
    <Card className="br" style={{ minHeight: "350px" }}>
      {isLoading ? (
        <div
          style={{
            minHeight: "350px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spinner
            size="lg"
            style={{ color: mode === "dark" ? "#fff" : "#2990DF" }}
          />
        </div>
      ) : (
        <CardBody style={{ minHeight: "50vh" }}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="card-title">Page Views</h4>

            <ButtonGroup>
              <Button
                size="sm"
                onClick={() => setDataGranularity("monthly")}
                style={{
                  minWidth: "150px",
                  backgroundColor:
                    dataGranularity === "monthly" ? "#2990DF" : "transparent",
                  color: dataGranularity === "monthly" ? "#fff" : "#666",
                  borderColor: "#2990DF",
                }}
              >
                Monthly (1 Year)
              </Button>

              <Button
                size="sm"
                onClick={() => setDataGranularity("daily")}
                style={{
                  minWidth: "150px",
                  backgroundColor:
                    dataGranularity === "daily" ? "#2990DF" : "transparent",
                  color: dataGranularity === "daily" ? "#fff" : "#666",
                  borderColor: "#2990DF",
                }}
              >
                Daily (2 Weeks)
              </Button>
            </ButtonGroup>
          </div>

          <Row>
            <ReactApexChart
              options={chartData.options}
              series={chartData.series}
              type="line"
              height="290"
            />
          </Row>
        </CardBody>
      )}
    </Card>
  )
}

export default ReachChart
